import { useEffect, useId, useState, type ReactNode } from 'react';

import {
  InfoPanelTabBar,
  type QuestionInfoPanelTab,
  type QuestionInfoPanelTabId,
} from './InfoPanelTabBar';
import styles from './QuestionInfoPanel.module.css';

interface QuestionInfoPanelProps {
  ariaLabel?: string;
  renderContent: (activeTab: QuestionInfoPanelTabId) => ReactNode;
  tabs: QuestionInfoPanelTab[];
}

function normalizeId(value: string) {
  return value.replace(/:/g, '');
}

export function QuestionInfoPanel({
  ariaLabel = 'Question information',
  renderContent,
  tabs,
}: QuestionInfoPanelProps) {
  const baseId = normalizeId(useId());
  const [activeTab, setActiveTab] = useState<QuestionInfoPanelTabId | null>(
    null,
  );
  const activeTabDefinition =
    activeTab === null ? null : tabs.find((tab) => tab.id === activeTab) ?? null;

  useEffect(() => {
    if (activeTab && !tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(null);
    }
  }, [activeTab, tabs]);

  if (tabs.length === 0) {
    return null;
  }

  const getTabId = (tabId: QuestionInfoPanelTabId) =>
    `${baseId}-question-info-${tabId}-tab`;
  const getPanelId = (tabId: QuestionInfoPanelTabId) =>
    `${baseId}-question-info-${tabId}-panel`;

  function handleTabSelect(tabId: QuestionInfoPanelTabId) {
    setActiveTab((current) => (current === tabId ? null : tabId));
  }

  return (
    <section
      aria-label={ariaLabel}
      className={styles.panel}
      data-expanded={activeTabDefinition ? 'true' : 'false'}
    >
      <InfoPanelTabBar
        activeTab={activeTab}
        getPanelId={getPanelId}
        getTabId={getTabId}
        onTabSelect={handleTabSelect}
        tabs={tabs}
      />

      <div
        aria-hidden={activeTabDefinition ? undefined : true}
        className={styles.contentShell}
        data-expanded={activeTabDefinition ? 'true' : 'false'}
      >
        <div className={styles.contentInner}>
          {activeTabDefinition ? (
            <div
              aria-labelledby={getTabId(activeTabDefinition.id)}
              className={styles.tabPanel}
              id={getPanelId(activeTabDefinition.id)}
              role="tabpanel"
            >
              {renderContent(activeTabDefinition.id)}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
